
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { exercicios } from '../../../mocks/exercicios';

interface ExercicioRecomendado {
  id: string;
  titulo: string;
  subtitulo: string;
  categoria: string;
  duracao: string;
  nivel: string;
  preco: number;
  imagem: string;
  badge?: string;
  motivo?: string;
}

export default function ExerciciosRecomendados() {
  const [recomendacoes, setRecomendacoes] = useState<ExercicioRecomendado[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    carregarRecomendacoes();
  }, []);

  const carregarRecomendacoes = async () => {
    try {
      // Obter usuário autenticado
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        console.error('Erro ao obter usuário:', userError);
        return;
      }

      if (!user) return;

      // Verificar perfil (admin ou plano premium)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin, plano')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Erro ao buscar perfil:', profileError);
        return;
      }

      const admin = profile?.is_admin === true || profile?.plano === 'premium';
      setIsAdmin(admin);

      // Caso admin, mostrar todos os exercícios como liberados
      if (admin) {
        const todosExercicios = exercicios.map((ex) => ({
          ...ex,
          badge: 'Liberado',
          motivo: 'Acesso total como administrador',
        }));
        setRecomendacoes(todosExercicios.slice(0, 6));
        return;
      }

      // Buscar pagamentos confirmados do usuário
      const { data: pagamentos, error: pagamentosError } = await supabase
        .from('pagamentos')
        .select('plano')
        .eq('user_id', user.id)
        .eq('status', 'paid');

      if (pagamentosError) {
        console.error('Erro ao buscar pagamentos:', pagamentosError);
        return;
      }

      // Determinar exercícios já adquiridos
      const exerciciosAdquiridos = new Set<string>();
      pagamentos?.forEach((pag) => {
        const plano = (pag.plano ?? '').toString().toLowerCase();

        if (plano.includes('ancoragem')) exerciciosAdquiridos.add('ancoragem-emocional');
        if (plano.includes('encantations') || plano.includes('tony robbins'))
          exerciciosAdquiridos.add('encantations-tony-robbins');
        if (plano.includes('swish') || plano.includes('switch')) exerciciosAdquiridos.add('tecnica-swish-pnl');
        if (plano.includes('ressignificacao') || plano.includes('reframing'))
          exerciciosAdquiridos.add('ressignificacao-reframing-pnl');
        if (plano.includes('linha') || plano.includes('timeline')) exerciciosAdquiridos.add('linha-tempo-pnl');
        if (plano.includes('submodalidades')) exerciciosAdquiridos.add('submodalidades-pnl');
        if (plano.includes('modelagem')) exerciciosAdquiridos.add('modelagem-excelencia-pnl');
        if (plano.includes('amor ideal')) exerciciosAdquiridos.add('amor-ideal');
        if (plano.includes('prosperidade') || plano.includes('carreira')) exerciciosAdquiridos.add('prosperidade-carreira');
        if (plano.includes('saude') || plano.includes('bem-estar')) exerciciosAdquiridos.add('saude-bem-estar');
        if (plano.includes('proposito')) exerciciosAdquiridos.add('proposito-vida');
        if (plano.includes('identidade') || plano.includes('alta performance'))
          exerciciosAdquiridos.add('identidade-alta-performance');

        // Pacote completo PNL
        if (plano.includes('pacote') || plano.includes('completo')) {
          exerciciosAdquiridos.add('ancoragem-emocional');
          exerciciosAdquiridos.add('encantations-tony-robbins');
          exerciciosAdquiridos.add('tecnica-swish-pnl');
          exerciciosAdquiridos.add('ressignificacao-reframing-pnl');
          exerciciosAdquiridos.add('linha-tempo-pnl');
          exerciciosAdquiridos.add('submodalidades-pnl');
        }
      });

      // ----------- Lógica de recomendação -----------
      const recomendados: ExercicioRecomendado[] = [];

      // 1. Completar coleção PNL caso já tenha algum
      const exerciciosPNL = [
        'ancoragem-emocional',
        'encantations-tony-robbins',
        'tecnica-swish-pnl',
        'ressignificacao-reframing-pnl',
        'linha-tempo-pnl',
        'submodalidades-pnl',
      ];
      const temPNL = exerciciosPNL.some((id) => exerciciosAdquiridos.has(id));
      const pnlFaltantes = exerciciosPNL.filter((id) => !exerciciosAdquiridos.has(id));

      if (temPNL && pnlFaltantes.length > 0) {
        pnlFaltantes.forEach((id) => {
          const ex = exercicios.find((e) => e.id === id);
          if (ex) {
            recomendados.push({
              ...ex,
              badge: 'Completa sua coleção PNL',
              motivo: 'Continue sua jornada de PNL',
            });
          }
        });
      }

      // 2. Se ainda não possui nenhum exercício, sugerir os mais populares
      if (exerciciosAdquiridos.size === 0) {
        const populares = ['ancoragem-emocional', 'tecnica-swish-pnl', 'encantations-tony-robbins'];
        populares.forEach((id) => {
          const ex = exercicios.find((e) => e.id === id);
          if (ex) {
            recomendados.push({
              ...ex,
              badge: 'Mais Popular',
              motivo: 'Perfeito para começar',
            });
          }
        });
      }

      // 3. Recomendar exercícios relacionados aos que já tem
      if (exerciciosAdquiridos.size > 0 && recomendados.length < 6) {
        const relacionados = new Set<string>();

        exerciciosAdquiridos.forEach((idAdquirido) => {
          const ex = exercicios.find((e) => e.id === idAdquirido);
          if (ex?.relacionados) {
            ex.relacionados.forEach((rel) => {
              if (!exerciciosAdquiridos.has(rel) && !recomendados.find((r) => r.id === rel)) {
                relacionados.add(rel);
              }
            });
          }
        });

        Array.from(relacionados)
          .slice(0, 6 - recomendados.length)
          .forEach((id) => {
            const ex = exercicios.find((e) => e.id === id);
            if (ex) {
              recomendados.push({
                ...ex,
                badge: 'Recomendado para você',
                motivo: 'Baseado nos seus exercícios',
              });
            }
          });
      }

      // 4. Completar até 6 itens com outros exercícios não adquiridos
      if (recomendados.length < 6) {
        const outros = exercicios.filter(
          (ex) => !exerciciosAdquiridos.has(ex.id) && !recomendados.find((r) => r.id === ex.id)
        );
        outros.slice(0, 6 - recomendados.length).forEach((ex) => {
          recomendados.push({
            ...ex,
            badge: 'Descubra',
            motivo: 'Expanda suas habilidades',
          });
        });
      }

      setRecomendacoes(recomendados.slice(0, 6));
    } catch (err) {
      console.error('Erro ao carregar recomendações:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse"></div>
          <div className="h-6 w-64 bg-white/10 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-96 rounded-2xl bg-white/5 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (recomendacoes.length === 0) {
    return null;
  }

  return (
    <div className="mb-10">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg">
            <i className="ri-lightbulb-flash-line text-white text-2xl"></i>
          </div>
          <div>
            <h2 className="text-white text-3xl font-bold font-['Playfair_Display']">
              {isAdmin ? 'Todos os Exercícios' : 'Exercícios Recomendados'}
            </h2>
            <p className="text-white/40 text-base mt-0.5">
              {isAdmin ? 'Acesso total liberado' : 'Personalizados para você'}
            </p>
          </div>
        </div>

        <Link
          to="/planos"
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-white text-lg font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <span>Ver Todos</span>
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {/* Grid de exercícios */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recomendacoes.map((exercicio) => (
          <Link
            key={exercicio.id}
            to={`/exercicio/${exercicio.id}`}
            className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer"
          >
            {/* Badge */}
            {exercicio.badge && (
              <div className="absolute top-4 right-4 z-10">
                <span
                  className={`px-3 py-1 rounded-full text-base font-semibold shadow-lg ${
                    exercicio.badge === 'Liberado'
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white'
                      : exercicio.badge === 'Completa sua coleção PNL'
                      ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white'
                      : exercicio.badge === 'Mais Popular'
                      ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white'
                  }`}
                >
                  {exercicio.badge}
                </span>
              </div>
            )}

            {/* Imagem */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={exercicio.imagem}
                alt={exercicio.titulo}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            </div>

            {/* Conteúdo */}
            <div className="p-5">
              {/* Categoria */}
              <span className="inline-block px-2 py-1 bg-purple-500/20 text-purple-300 text-base rounded-lg mb-2">
                {exercicio.categoria}
              </span>

              {/* Título */}
              <h3 className="text-white text-xl font-bold mb-2 line-clamp-2 group-hover:text-purple-300 transition-colors">
                {exercicio.titulo}
              </h3>

              {/* Subtítulo */}
              <p className="text-white/60 text-base mb-4 line-clamp-2">{exercicio.subtitulo}</p>

              {/* Motivo da recomendação */}
              {exercicio.motivo && (
                <div className="flex items-center gap-2 text-purple-300 text-base mb-4">
                  <i className="ri-sparkling-line"></i>
                  <span>{exercicio.motivo}</span>
                </div>
              )}

              {/* Informações */}
              <div className="flex items-center justify-between text-white/40 text-base mb-4">
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>{exercicio.duracao}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-bar-chart-line"></i>
                  <span>{exercicio.nivel}</span>
                </div>
              </div>

              {/* Preço e CTA */}
              <div className="flex items-center justify-between">
                {isAdmin ? (
                  <span className="text-amber-400 text-2xl font-bold">Liberado ✓</span>
                ) : (
                  <span className="text-white text-2xl font-bold">
                    R$ {exercicio.preco.toFixed(2).replace('.', ',')}
                  </span>
                )}
                <div className="flex items-center gap-2 text-purple-300 font-semibold text-lg group-hover:gap-3 transition-all">
                  <span>Ver Exercício</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Dica */}
      {!isAdmin && (
        <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
          <i className="ri-information-line text-purple-300 text-xl mt-0.5"></i>
          <div>
            <p className="text-white/80 text-lg">
              <strong className="text-purple-300">Dica:</strong> As recomendações são personalizadas
              com base nos exercícios que você já possui e no seu perfil de aprendizado.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
